import type { PostHogContextRef } from '../context'

/**
 * Community shared-link virality events. Fire from the shared-playlist page
 * (`/playlist/shared/:id`) — these are the *inbound* signals that pair with
 * the outbound `community_playlist_shared` to close the virality loop:
 *

 *   share (outbound)         →  community_playlist_shared            { playlist_title, method }
 *   open shared link         →  community_shared_playlist_viewed
 *                                { playlist_title, is_logged_in }
 *   anon viewer → signup     →  community_shared_link_signup_clicked { playlist_title }
 *   member viewer → follow   →  community_shared_link_followed       { playlist_title }
 *   viewer → creator profile →  community_shared_link_creator_clicked
 *                                { playlist_title, creator_display_name? }
 *
 * `community_shared_link_followed` fires *alongside* the generic
 * `community_playlist_followed` when the follow originates from a shared-link
 * landing — the two events answer different questions (outbound-engagement vs
 * shared-link conversion) and are intended to live side-by-side.
 *
 * All shared-link events carry the playlist *title* (not its id) for
 * dashboard legibility, and the creator's *display name* (not their UUID) —
 * UUIDs are intentionally never captured for third-party entities.
 */
export type SharedLinkEvents = {
  community_shared_link_signup_clicked: (playlistTitle: string) => void
  community_shared_link_followed: (playlistTitle: string) => void
  community_shared_link_creator_clicked: (
    playlistTitle: string,
    creatorDisplayName?: string,
  ) => void
}

export function createSharedLinkEventCallbacks (
  contextRef: PostHogContextRef,
): SharedLinkEvents {
  return {
    community_shared_link_signup_clicked: (playlistTitle: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_shared_link_signup_clicked', {
        context: 'community',
        playlist_title: playlistTitle,
      })
    },

    community_shared_link_followed: (playlistTitle: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_shared_link_followed', {
        context: 'community',
        playlist_title: playlistTitle,
      })
    },

    community_shared_link_creator_clicked: (
      playlistTitle: string,
      creatorDisplayName?: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_shared_link_creator_clicked', {
        context: 'community',
        playlist_title: playlistTitle,
        ...(creatorDisplayName && { creator_display_name: creatorDisplayName }),
      })
    },
  }
}
